import { Component } from '@theme/component';

// A bounded reader for our verified, uncompressed geometry-only GLB derivative.
// No arbitrary material, texture, script, scene graph or external URI is executed.
export function readBModel(buffer) {
  const view = new DataView(buffer);
  if (view.getUint32(0, true) !== 0x46546c67 || view.getUint32(4, true) !== 2 || view.getUint32(8, true) !== buffer.byteLength) throw new Error('Invalid B model');
  const size = view.getUint32(12, true);
  const doc = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 20, size)));
  const binaryOffset = 28 + size;
  if (view.getUint32(16, true) !== 0x4e4f534a || view.getUint32(24 + size, true) !== 0x004e4942 || doc.extensionsRequired?.length) throw new Error('Unsupported B model');
  const primitive = doc.meshes?.[0]?.primitives?.[0];
  if (!primitive || (primitive.mode !== undefined && primitive.mode !== 4)) throw new Error('Missing B geometry');
  const position = doc.accessors[primitive.attributes.POSITION];
  const normal = doc.accessors[primitive.attributes.NORMAL];
  const index = doc.accessors[primitive.indices];
  if (position.componentType !== 5126 || normal.componentType !== 5126 || position.count !== normal.count || position.count > 250000 || index.count > 750000) throw new Error('Unsupported B geometry');
  const pv = doc.bufferViews[position.bufferView], nv = doc.bufferViews[normal.bufferView], iv = doc.bufferViews[index.bufferView];
  const vertices = new Float32Array(position.count * 6);
  const center = position.min.map((n, i) => (n + position.max[i]) / 2);
  const scale = 2 / Math.max(...position.max.map((n, i) => n - position.min[i]));
  for (let i = 0; i < position.count; i++) {
    const po = binaryOffset + (pv.byteOffset || 0) + (position.byteOffset || 0) + i * (pv.byteStride || 12);
    const no = binaryOffset + (nv.byteOffset || 0) + (normal.byteOffset || 0) + i * (nv.byteStride || 12);
    for (let j = 0; j < 3; j++) {
      vertices[i * 6 + j] = (view.getFloat32(po + j * 4, true) - center[j]) * scale;
      vertices[i * 6 + j + 3] = view.getFloat32(no + j * 4, true);
    }
  }
  const indices = position.count <= 65535 ? new Uint16Array(index.count) : new Uint32Array(index.count);
  const stride = index.componentType === 5125 ? 4 : index.componentType === 5123 ? 2 : 0;
  if (!stride) throw new Error('Unsupported B indices');
  for (let i = 0; i < indices.length; i++) {
    const offset = binaryOffset + (iv.byteOffset || 0) + (index.byteOffset || 0) + i * stride;
    const n = stride === 4 ? view.getUint32(offset, true) : view.getUint16(offset, true);
    if (n >= position.count) throw new Error('Invalid B index');
    indices[i] = n;
  }
  return { vertices, indices };
}

const vertexSource = `
attribute vec3 position; attribute vec3 normal;
uniform vec2 turn; uniform float aspect;
varying vec3 n; varying vec3 p;
void main(){
 float x=turn.x,y=turn.y;
 mat3 rx=mat3(1.,0.,0.,0.,cos(x),sin(x),0.,-sin(x),cos(x));
 mat3 ry=mat3(cos(y),0.,-sin(y),0.,1.,0.,sin(y),0.,cos(y));
 mat3 camera=mat3(.933,.076,.352,0.,.978,-.213,-.36,.196,.912);
 p=camera*rx*ry*position; n=camera*rx*ry*normal;
 gl_Position=vec4(p.x/(1.34*aspect),p.y/1.34,-p.z/6.,1.);
}`;
const fragmentSource = `
precision mediump float; varying vec3 n; varying vec3 p;
void main(){
 vec3 N=normalize(n); if(!gl_FrontFacing)N=-N;
 vec3 V=normalize(vec3(0.,0.,4.)-p);
 vec3 L=normalize(vec3(-.5,1.,1.4));
 float diffuse=max(dot(N,L),0.);
 float rim=pow(1.-max(dot(N,V),0.),3.);
 vec3 R=reflect(-V,N);
 float strip=pow(max(0.,1.-abs(R.y-.32)*2.6),9.);
 float highlight=pow(max(dot(reflect(-L,N),V),0.),48.);
 vec3 ink=vec3(.055,.058,.052)*(0.65+diffuse*.9);
 vec3 color=ink+vec3(.42,.44,.39)*strip+vec3(.7)*highlight*.6+vec3(.18,.2,.12)*rim;
 gl_FragColor=vec4(pow(color,vec3(.82)),1.);
}`;

class BarryContactObject extends Component {
  connectedCallback() { super.connectedCallback(); this.setup(); }
  updatedCallback() { this.cleanup(); super.updatedCallback(); this.setup(); }
  setup() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.canvas = this.querySelector('canvas');
    this.querySelector('[data-enable]').addEventListener('click',()=>this.load(),{signal});
    this.target = [0, 0]; this.turn = [0, 0]; this.static = false;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.visible = false;
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      if (this.visible && !this.loaded && !this.loading && !this.reduced.matches && !navigator.connection?.saveData) this.load();
      if (this.visible && this.loaded && !this.static) this.requestRender();
    }, { threshold: .1 });
    this.observer.observe(this);
    this.reduced.addEventListener('change', () => {
      if (this.reduced.matches) this.setStatic(true);
      else if (!this.loaded && this.visible) this.load();
    }, { signal });
    window.addEventListener('pointermove', event => {
      if (event.pointerType !== 'mouse' || !this.visible || this.static || this.reduced.matches) return;
      this.target = [-(event.clientY / innerHeight - .5) * .36, (event.clientX / innerWidth - .5) * .8];
      this.requestRender();
    }, { signal, passive: true });
    this.canvas.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse' || this.static) return;
      this.drag = { x: event.clientX, y: this.target[1] };
    }, { signal, passive: true });
    this.canvas.addEventListener('pointermove', event => {
      if (!this.drag || this.static) return;
      this.target[1] = Math.max(-.7, Math.min(.7, this.drag.y + (event.clientX - this.drag.x) / 220));
      this.requestRender();
    }, { signal, passive: true });
    for (const name of ['pointerup', 'pointercancel']) window.addEventListener(name, () => { this.drag = null; }, { signal, passive: true });
    this.canvas.addEventListener('keydown', event => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home'].includes(event.key) || this.static) return;
      event.preventDefault();
      if (event.key === 'Home') this.target = [0,0];
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') this.target[1] = Math.max(-.7,Math.min(.7,this.target[1]+(event.key==='ArrowLeft'?-.15:.15)));
      else this.target[0] = Math.max(-.35,Math.min(.35,this.target[0]+(event.key==='ArrowUp'?-.1:.1)));
      this.requestRender();
    }, { signal });
    this.querySelectorAll('[data-turn]').forEach(button => button.addEventListener('click', () => {
      if (this.static) return;
      this.target[1] = Math.max(-.7,Math.min(.7,this.target[1]+Number(button.dataset.turn)*.2));this.requestRender();
    }, { signal }));
    this.querySelector('[data-reset]').addEventListener('click', () => {this.target=[0,0];this.requestRender();}, { signal });
    this.querySelector('[data-static]').addEventListener('click', () => this.setStatic(!this.static), { signal });
    this.canvas.addEventListener('webglcontextlost', event => {event.preventDefault();this.setStatic(true);}, { signal });
    document.addEventListener('visibilitychange', () => {if (!document.hidden) this.requestRender();}, { signal });
  }
  async load() {
    if (this.loading || this.loaded) return;
    this.loading = true;
    const enable = this.querySelector('[data-enable]');enable.disabled=true;
    const signal = this.controller.signal;
    try {
      const response = await fetch(this.dataset.model, { signal });
      if (!response.ok) throw new Error('B unavailable');
      const model = readBModel(await response.arrayBuffer());
      if (signal.aborted) return;
      const gl = this.canvas.getContext('webgl', {alpha:true,antialias:true,powerPreference:'low-power'});
      if (!gl || (model.indices instanceof Uint32Array && !gl.getExtension('OES_element_index_uint'))) throw new Error('WebGL unavailable');
      this.gl=gl;
      const compile = (type,source) => {const s=gl.createShader(type);gl.shaderSource(s,source);gl.compileShader(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS))throw new Error('B shader unavailable');return s;};
      const vs=compile(gl.VERTEX_SHADER,vertexSource),fs=compile(gl.FRAGMENT_SHADER,fragmentSource),program=gl.createProgram();
      gl.attachShader(program,vs);gl.attachShader(program,fs);gl.linkProgram(program);gl.deleteShader(vs);gl.deleteShader(fs);
      if(!gl.getProgramParameter(program,gl.LINK_STATUS))throw new Error('B renderer unavailable');
      this.program=program;gl.useProgram(program);
      this.vertexBuffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,this.vertexBuffer);gl.bufferData(gl.ARRAY_BUFFER,model.vertices,gl.STATIC_DRAW);
      for (const [name,offset] of [['position',0],['normal',12]]) {const loc=gl.getAttribLocation(program,name);gl.enableVertexAttribArray(loc);gl.vertexAttribPointer(loc,3,gl.FLOAT,false,24,offset);}
      this.indexBuffer=gl.createBuffer();gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,this.indexBuffer);gl.bufferData(gl.ELEMENT_ARRAY_BUFFER,model.indices,gl.STATIC_DRAW);
      this.count=model.indices.length;this.indexType=model.indices instanceof Uint16Array?gl.UNSIGNED_SHORT:gl.UNSIGNED_INT;
      this.turnUniform=gl.getUniformLocation(program,'turn');this.aspectUniform=gl.getUniformLocation(program,'aspect');gl.enable(gl.DEPTH_TEST);gl.clearColor(0,0,0,0);
      this.querySelector('.bc-object-poster').setAttribute('aria-hidden','true');
      this.loaded=true;this.dataset.ready='true';this.canvas.hidden=false;this.querySelector('.bc-object-controls').hidden=false;enable.hidden=true;
      this.resizeObserver=new ResizeObserver(()=>this.requestRender());this.resizeObserver.observe(this.canvas);this.requestRender();
    } catch {if (!signal.aborted) {this.dataset.ready='false';this.canvas.hidden=true;this.releaseGL();}}
    finally {this.loading=false;enable.disabled=false;}
  }
  setStatic(value) {
    this.static=value;this.querySelector('.bc-object-poster').setAttribute('aria-hidden',String(!value && this.loaded));this.dataset.static=String(value);this.canvas.hidden=value||!this.loaded;
    const button=this.querySelector('[data-static]');button.textContent=value?button.dataset.interactiveLabel:button.dataset.staticLabel;button.setAttribute('aria-pressed',String(value));
    for(const b of this.querySelectorAll('[data-turn],[data-reset]'))b.disabled=value;
    if(!value&&!this.loaded)this.load();else this.requestRender();
  }
  requestRender() {if(!this.frame&&this.loaded&&this.visible&&!document.hidden&&!this.static)this.frame=requestAnimationFrame(()=>this.draw());}
  draw() {
    this.frame=null;if(!this.gl||this.static||!this.visible||document.hidden)return;
    const gl=this.gl,rect=this.canvas.getBoundingClientRect(),ratio=Math.min(devicePixelRatio||1,1.5),w=Math.round(rect.width*ratio),h=Math.round(rect.height*ratio);
    if(!w||!h)return;
    if(this.canvas.width!==w||this.canvas.height!==h){this.canvas.width=w;this.canvas.height=h;gl.viewport(0,0,w,h);}
    let moving=false;
    for(let i=0;i<2;i++){const d=this.target[i]-this.turn[i];this.turn[i]+=this.reduced.matches?d:d*.15;if(Math.abs(d)>.001)moving=true;}
    gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);gl.uniform2f(this.turnUniform,...this.turn);gl.uniform1f(this.aspectUniform,w/h);gl.drawElements(gl.TRIANGLES,this.count,this.indexType,0);
    this.dataset.turn=this.turn.map(n=>n.toFixed(3)).join(',');
    if(moving&&!this.reduced.matches)this.requestRender();
  }
  releaseGL() {if(this.gl){this.gl.deleteBuffer(this.vertexBuffer);this.gl.deleteBuffer(this.indexBuffer);this.gl.deleteProgram(this.program);}this.gl=null;}
  cleanup() {this.controller?.abort();this.observer?.disconnect();this.resizeObserver?.disconnect();cancelAnimationFrame(this.frame);this.frame=null;this.releaseGL();this.loaded=false;this.loading=false;}
  disconnectedCallback() {this.cleanup();super.disconnectedCallback();}
}
if(!customElements.get('bc-contact-object'))customElements.define('bc-contact-object',BarryContactObject);
